if (!customElements.get('collection-infinite-scroll')) {
  customElements.define(
    'collection-infinite-scroll',
    class extends HTMLElement {
      connectedCallback() {
        if (!('IntersectionObserver' in window)) return;

        this.section = this.closest('.shopify-section');
        this.grid = this.section?.querySelector('[data-infinite-grid]');
        this.pagination = this.section?.querySelector(
          '[data-infinite-pagination]'
        );
        this.status = this.querySelector('[role="status"]');
        this.nextUrl = this.dataset.nextUrl;
        this.loading = false;

        if (!this.section?.id || !this.grid || !this.nextUrl) return;

        this.abortController = new AbortController();

        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.loadNextPage();
            }
          },
          { rootMargin: '0px 0px 400px 0px' }
        );

        if (this.pagination) this.pagination.hidden = true;
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.abortController?.abort();
      }

      async loadNextPage() {
        if (this.loading || !this.nextUrl || !this.isConnected) return;

        this.loading = true;
        this.observer.unobserve(this);

        if (this.status) this.status.hidden = false;

        try {
          const url = new URL(this.nextUrl, window.location.origin);

          if (url.origin !== window.location.origin) {
            throw new Error('Unexpected pagination URL');
          }

          const response = await fetch(url.href, {
            signal: this.abortController.signal,
            credentials: 'same-origin'
          });

          if (!response.ok) {
            throw new Error('Could not load next page');
          }

          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const section = doc.getElementById(this.section.id);
          const nextGrid = section?.querySelector('[data-infinite-grid]');
          const nextMarker = section?.querySelector(
            'collection-infinite-scroll'
          );
          const nextPagination = section?.querySelector(
            '[data-infinite-pagination]'
          );

          if (!nextGrid || !nextMarker || !nextGrid.children.length) {
            throw new Error('Product grid was not found');
          }

          if (!this.isConnected || !this.grid.isConnected) return;

          const newItems = Array.from(nextGrid.children, (item) => {
            const copy = document.importNode(item, true);

            // Newly appended cards should remain visible without
            // depending on the theme's scroll-animation observer.
            const animatedElements = [
              copy,
              ...copy.querySelectorAll('.scroll-trigger')
            ];

            animatedElements.forEach((element) => {
              element.classList.remove(
                'scroll-trigger',
                'scroll-trigger--offscreen',
                'animate--slide-in',
                'animate--fade-in'
              );
              element.removeAttribute('data-cascade');
            });

            return copy;
          });

          this.grid.append(...newItems);

          if (this.pagination && nextPagination) {
            this.pagination.replaceChildren(
              ...Array.from(nextPagination.childNodes, (node) =>
                document.importNode(node, true)
              )
            );
          }

          this.nextUrl = nextMarker.dataset.nextUrl || '';
          this.dataset.nextUrl = this.nextUrl;
        } catch (error) {
          if (error.name === 'AbortError') return;

          this.nextUrl = '';

          if (this.pagination) this.pagination.hidden = false;

          console.warn('Automatic product loading stopped:', error);
        } finally {
          this.loading = false;

          if (this.status) this.status.hidden = true;

          if (this.nextUrl && this.isConnected) {
            this.observer.observe(this);
          }
        }
      }
    }
  );
}